import { useCallback, useEffect, useState } from 'react';
import { reviewService } from '@/services/reviewService';
import { toReviewError, type ReviewError } from '../utils/errorMap';
import type { ReviewableResource } from '../types/resource';

interface UseReviewableResourcesOptions {
  /**
   * Program filter forwarded to the backend as `?program=...`. A
   * single code (`RF`) or CSV of codes (`SD,RGSH,RGSF,RGST,RGSG`
   * for Sports) — the server narrows the list before responding.
   * Omit to fetch the full role-filtered list.
   */
  program?: string;
}

interface UseReviewableResourcesReturn {
  resources: ReviewableResource[];
  loading: boolean;
  error: ReviewError | null;
  refetch: () => void;
}

/**
 * Load the resources the current user is allowed to review.
 *
 * Refetches whenever `options.program` changes so that clicking
 * between Programs in the sidebar always shows a fresh, server-
 * narrowed list. A `nonce` counter also allows manual refetches
 * after mutations elsewhere that might have changed the user's
 * assignment.
 *
 * Loading defaults to `true` so consumers don't render an empty
 * state on the very first paint before the initial request has
 * been fired.
 *
 * The cleanup uses a mounted-flag guard (not an AbortSignal) because
 * React 18 Strict Mode double-invokes effects in dev, and aborting
 * on the "test" cleanup can cancel the request axios was in the
 * middle of preparing — leaving the second invocation with a fresh
 * controller that racy timing can also cancel. A mounted flag has
 * no such race: only setState is skipped after unmount; the network
 * request itself is allowed to complete.
 */
export function useReviewableResources(
  options?: UseReviewableResourcesOptions,
): UseReviewableResourcesReturn {
  const program = options?.program;

  const [resources, setResources] = useState<ReviewableResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ReviewError | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    reviewService
      .getResources({ program })
      .then((data) => {
        if (cancelled) return;
        setResources(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(toReviewError(err));
        setResources([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nonce, program]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return { resources, loading, error, refetch };
}
